import React, { FC, ReactElement, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { TextLink } from '@sdir/sds';
import { DescriptionBox, PageTitle } from '../../Atoms';
import CardWithIcon from '../../Atoms/CardWithIcon';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

interface InfoPageLayoutProps {
  children: any;
  title: string;
  description: string | ReactNode;
  infoTitle?: string;
  icon?: ReactElement;
  extraInfoTitle?: string;
  extraInfoDescription?: string | ReactNode;
}

export const InfoPageLayout: FC<InfoPageLayoutProps> = ({
  title,
  infoTitle,
  icon,
  description,
  children,
  extraInfoTitle,
  extraInfoDescription,
}) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();

  const backToDashboard = useCallback(() => {
    navigate(routes.home);
  }, [navigate]);

  return (
    <Wrapper>
      <PageSubheader>
        <PageTitle title={title} />
        <TextLink
          size="2rem"
          text={intl.formatMessage({ id: 'back.to.dashboard' })}
          click={backToDashboard}
        />
      </PageSubheader>
      <CardContainer>
        <StyledCardWithIcon iconIncluded={!!icon} reactIcon={icon}>
          <Description>
            <H2>{infoTitle || title}</H2>
            <p>{description}</p>
          </Description>
        </StyledCardWithIcon>
        {(extraInfoTitle || extraInfoDescription) && (
          <StyledDescriptionBox header={extraInfoTitle || ''} text={extraInfoDescription || ''} />
        )}
      </CardContainer>
      <ChildWrapper>{children}</ChildWrapper>
    </Wrapper>
  );
};

const CardContainer = styled.div`
  display: flex;
  gap: 32px;
  flex-direction: row;
  justify-content: stretch;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 32px;
  margin-top: 24px;
`;

const PageSubheader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 1em 0;
`;

const StyledCardWithIcon = styled(CardWithIcon)`
  width: 480px;
  margin-top: 0;
  margin-bottom: 0;
  flex: 100 0 auto;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
    flex-direction: column;
    align-items: center;

    & > span {
      width: 96px;
      height: 96px;
    }

    svg {
      width: 48px;
      height: 48px;
    }
  }
`;

const StyledDescriptionBox = styled(DescriptionBox)`
  width: 480px;
  margin-bottom: 0;
  flex: 1 0 auto;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  display: grid;
  margin: 3rem auto;
  h1 {
    margin-bottom: 0;
  }
`;

const ChildWrapper = styled.div`
  max-width: 100vw;
`;

const Description = styled.div`
  max-width: 720px;
  p {
    line-height: 3rem;
    margin-top: 0.5em;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
    max-width: 576px;
  }
  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
  }
`;

const H2 = styled.h2`
  font-size: 3rem;
  margin-top: 0.5em;
  margin-bottom: 0;
`;
